// Class for the remove button in the cart
class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    // Add click event listener to the button
    this.addEventListener("click", this.onClick.bind(this));
  }

  // Handle the click event
  onClick(event) {
    event.preventDefault();

    // Find the cart form or cart drawer content element
    const cartItems =
      this.closest("cart-form") || this.closest("cart-drawer-content");
    // Update the quantity of the item to 0
    cartItems.updateQuantity(this.dataset.index, 0);
  }
}

customElements.define("cart-remove-button", CartRemoveButton);

// Class for the cart form
class CartForm extends HTMLElement {
  constructor() {
    super();

    // Initialize the debouncedOnChange function with a debounce of 300ms
    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    // Add change event listener to the form
    this.addEventListener("change", this.debouncedOnChange.bind(this));

    // Get references to important elements
    this.lineItemStatusElement = document.getElementById("Cart-LineItemStatus");
    this.cartStatus = document.getElementById("Cart-LiveRegionText");

    // Calculate the current item count
    this.currentItemCount = Array.from(
      this.querySelectorAll('[name="updates[]"]')
    ).reduce(
      (total, quantityInput) => total + parseInt(quantityInput.value),
      0
    );
  }

  // Handle the change event
  onChange(event) {
    // Check if the change is related to the cart note
    if (
      event.target.id === "Cart-Note" ||
      event.target.id === "DrawerCart-Note"
    ) {
      // Update the cart note
      const body = JSON.stringify({ note: event.target.value });
      fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      return;
    }

    // Skip discount input fields - they have their own handlers
    if (event.target.classList.contains("cart-discount-input")) {
      return;
    }

    if (event.target.matches(".quantity__input")) {
      const parsedValue = parseInt(event.target.value);
      const min = parseInt(event.target.getAttribute("min"));
      const max = window.SkinNoteCartLimit?.maxPerVariant;

      if (!Number.isFinite(parsedValue) || (Number.isFinite(min) && parsedValue < min)) {
        event.target.value = event.target.getAttribute("value") || (Number.isFinite(min) ? min : 1);
        return;
      }

      if (Number.isFinite(max) && parsedValue > max && event.target.dataset.quantityVariantId) {
        event.target.value = max;
        this.updateLiveRegions(event.target.dataset.index, window.SkinNoteCartLimit.message(max));
      }
    }

    // Update the quantity of the item
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute("name")
    );
  }

  // Update the quantity of an item
  updateQuantity(line, quantity, name) {
    const max = window.SkinNoteCartLimit?.maxPerVariant;
    const requestedQuantity = parseInt(quantity);

    if (Number.isFinite(max) && Number.isFinite(requestedQuantity) && requestedQuantity > max) {
      quantity = max;
      this.updateLiveRegions(line, window.SkinNoteCartLimit.message(max));
    }

    // Enable loading state
    this.enableLoading();
    document.querySelector("cart-drawer")?.markUpdating?.();

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Cart-Quantity-${line}`);
        const items = document.querySelectorAll(".cart-item");

        if (parsedState.errors) {
          // Restore the previous value
          quantityElement.value = quantityElement.getAttribute("value");
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle("is-empty", parsedState.item_count === 0);

        // Update the sections in the cart
        this.getSectionsToRender().forEach((section) => {
          if (section.id == "Cart") {
            const elementsToReplace = document
              .getElementById(section.id)
              .querySelectorAll(section.selector);
            const mainCartParsed =
              parsedState.sections[document.getElementById("Cart").dataset.id];
            elementsToReplace.forEach((el) => {
              el.innerHTML = this.getSectionInnerHTMLWithDataset(
                mainCartParsed,
                section.selector,
                el.dataset.content
              );
            });
            return;
          }
          const elementToReplace =
            document
              .getElementById(section.id)
              .querySelector(section.selector) ||
            document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });

        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
        let message = "";
        if (
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          if (typeof updatedValue === "undefined") {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace(
              "[quantity]",
              updatedValue
            );
          }
        }
        this.updateLiveRegions(line, message);
      })
      .catch(() => {
        const errors = document.getElementById("Cart-Errors");
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading();
        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer) {
          cartDrawer.syncOpenState?.();
          cartDrawer.clearUpdating?.();
        }
      });
  }

  // Enable loading state
  enableLoading() {
    const cart =
      document.getElementById("Cart") || document.getElementById("CartDrawer");
    if (!cart) return;

    if (cart.id === "CartDrawer") {
      cart.querySelector("cart-form")?.classList.add("disable-pointer-events");
      cart.querySelector(".cart-drawer__footer")?.classList.add("disable-pointer-events");
      return;
    }

    cart.classList.add("disable-pointer-events");
  }

  // Disable loading state
  disableLoading() {
    const cart =
      document.getElementById("Cart") || document.getElementById("CartDrawer");
    if (!cart) return;

    cart.classList.remove("disable-pointer-events");
    cart.querySelector("cart-form")?.classList.remove("disable-pointer-events");
    cart.querySelector(".cart-drawer__footer")?.classList.remove("disable-pointer-events");
    cart.querySelectorAll("quantity-input.is-updating").forEach((quantityInput) => {
      quantityInput.unlockControls?.();
    });
  }

  // Update the live regions for accessibility
  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`Cart-LineItemError-${line}`);

    if (lineItemError) {
      const errorMessage = lineItemError.querySelector("small");
      const hasMessage = Boolean(message);
      if (errorMessage) errorMessage.innerHTML = message;
      lineItemError.classList.toggle("is-visible", hasMessage);
      lineItemError.closest(".cart-item__quantity")?.classList.toggle("has-line-item-error", hasMessage);
    }

    this.lineItemStatusElement.setAttribute("aria-hidden", true);
    this.cartStatus.setAttribute("aria-hidden", false);

    // Hide the cart status after 1 second
    setTimeout(() => {
      this.cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  // Get the sections to render
  getSectionsToRender() {
    return [
      {
        id: "Cart",
        section: document.getElementById("Cart").dataset.id,
        selector: ".js-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }

  // Get the inner HTML of a section
  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  // Get the inner HTML of a section with a specific dataset
  getSectionInnerHTMLWithDataset(html, selector, dataset) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(`[data-content="${dataset}"]`).innerHTML;
  }
}

// Define the custom element for the cart form
customElements.define("cart-form", CartForm);

// Function to apply discount code
let discountRequestCtrl = null;

function getCurrentDiscountCodes(context) {
  try {
    const scope =
      context === "drawer"
        ? document.getElementById("CartDrawer")
        : document.getElementById("Cart");
    const nodes = (scope || document).querySelectorAll(
      `cart-discount-removal[data-context="${context}"]`
    );
    const codes = Array.from(nodes)
      .map((n) => n.getAttribute("data-discount-code"))
      .filter(Boolean);
    return Array.from(new Set(codes));
  } catch (e) {
    return [];
  }
}

function applyDiscount(discountCode, context) {
  if (!discountCode || discountCode.trim() === "") {
    showDiscountError("", context, "code");
    return;
  }

  const discountComponent =
    context === "drawer"
      ? document.querySelector('cart-discount-component[context="drawer"]')
      : document.querySelector('cart-discount-component[context="main"]');

  if (discountComponent) {
    discountComponent.clearError();
  } else {
    const errorElement =
      context === "drawer"
        ? document.getElementById("DrawerCart-DiscountError")
        : document.getElementById("Cart-DiscountError");

    if (errorElement) {
      errorElement.style.display = "none";
    }
  }

  if (discountRequestCtrl) {
    try {
      discountRequestCtrl.abort();
    } catch (e) {}
  }
  discountRequestCtrl = new AbortController();

  const existingCodes = getCurrentDiscountCodes(context);
  const nextCodes = existingCodes.includes(discountCode.trim())
    ? existingCodes
    : [...existingCodes, discountCode.trim()];

  const body = JSON.stringify({
    discount: nextCodes.join(","),
    sections: getSectionsToRender().map((section) => section.section),
    sections_url: window.location.pathname,
  });

  fetch(`${routes.cart_update_url}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: body,
    signal: discountRequestCtrl.signal,
  })
    .then((response) => response.text())
    .then((state) => {
      const parsedState = JSON.parse(state);

      // Check if there are actual errors (not just the presence of an errors property)
      if (parsedState.errors && Object.keys(parsedState.errors).length > 0) {
        const errorMessage =
          typeof parsedState.errors === "string"
            ? parsedState.errors
            : parsedState.errors.discounts || "";
        showDiscountError(errorMessage, context, "code");
      } else {
        // Success - update cart sections without page reload
        getSectionsToRender().forEach((section) => {
          if (section.id === "Cart") {
            const elementsToReplace = document
              .getElementById(section.id)
              .querySelectorAll(section.selector);
            const mainCartParsed =
              parsedState.sections[document.getElementById("Cart").dataset.id];

            elementsToReplace.forEach((el) => {
              el.innerHTML = getSectionInnerHTMLWithDataset(
                mainCartParsed,
                section.selector,
                el.dataset.content
              );
            });
          } else {
            const sectionElement = document.getElementById(section.id);
            if (!sectionElement) {
              return;
            }

            const elementToReplace =
              sectionElement.querySelector(section.selector) || sectionElement;
            const sectionHTML = parsedState.sections[section.section];

            if (elementToReplace && sectionHTML) {
              elementToReplace.innerHTML = getSectionInnerHTML(
                sectionHTML,
                section.selector
              );
            }
          }
        });

        const prevCodes = existingCodes.map((c) => c.toUpperCase());
        const newCodes = getCurrentDiscountCodes(context).map((c) =>
          c.toUpperCase()
        );
        const attemptedCode = discountCode.trim().toUpperCase();
        const attemptedAdded =
          newCodes.includes(attemptedCode) &&
          !prevCodes.includes(attemptedCode);

        const attemptedEntry = Array.isArray(parsedState.discount_codes)
          ? parsedState.discount_codes.find(
              (dc) => (dc.code || "").toUpperCase() === attemptedCode
            )
          : null;

        if (attemptedEntry && attemptedEntry.applicable === false) {
          showDiscountError("", context, "code");
          if (discountComponent) {
            discountComponent.clearInput();
          } else {
            const discountInput =
              context === "drawer"
                ? document.getElementById("DrawerCart-DiscountInput")
                : document.getElementById("Cart-DiscountInput");
            if (discountInput) discountInput.value = "";
          }
          return;
        }

        if (!attemptedAdded) {
          showDiscountError("", context, "shipping");
          if (discountComponent) {
            discountComponent.clearInput();
          } else {
            const discountInput =
              context === "drawer"
                ? document.getElementById("DrawerCart-DiscountInput")
                : document.getElementById("Cart-DiscountInput");
            if (discountInput) discountInput.value = "";
          }
        } else {
          if (discountComponent) {
            discountComponent.clearInput();
          } else {
            const discountInput =
              context === "drawer"
                ? document.getElementById("DrawerCart-DiscountInput")
                : document.getElementById("Cart-DiscountInput");
            if (discountInput) discountInput.value = "";
          }
        }
      }
    })
    .catch((error) => {
      console.error("Error applying discount:", error);
      showDiscountError("", context, "code");
    })
    .finally(() => {
      discountRequestCtrl = null;
    });
}

// Helper function to get sections to render
function getSectionsToRender() {
  const sections = [
    {
      id: "cart-icon-bubble",
      section: "cart-icon-bubble",
      selector: ".shopify-section",
    },
    {
      id: "CartDrawer",
      section: "cart-drawer",
      selector: ".cart-drawer__inner",
    },
  ];

  // Only add the main cart section if we're on the cart page
  const cartElement = document.getElementById("Cart");
  if (cartElement && cartElement.dataset.id) {
    sections.unshift({
      id: "Cart",
      section: cartElement.dataset.id,
      selector: ".js-contents",
    });
  }

  return sections;
}

// Helper function to get section inner HTML
function getSectionInnerHTML(html, selector) {
  try {
    const element = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);

    if (!element) {
      return "";
    }

    return element.innerHTML;
  } catch (error) {
    return "";
  }
}

// Helper function to get section inner HTML with dataset
function getSectionInnerHTMLWithDataset(html, selector, dataset) {
  try {
    const htmlObject = new DOMParser().parseFromString(html, "text/html");

    // First try to find the element directly (like CartForm does)
    const datasetElement = htmlObject.querySelector(
      `[data-content="${dataset}"]`
    );

    if (datasetElement) {
      return datasetElement.innerHTML;
    }

    return "";
  } catch (error) {
    return "";
  }
}

function renderMainCartFromState(parsedState) {
  const cartElement = document.getElementById("Cart");
  if (!cartElement || !cartElement.dataset.id) return;

  const mainCartParsed = parsedState.sections?.[cartElement.dataset.id];
  if (!mainCartParsed) return;

  cartElement.querySelectorAll(".js-contents").forEach((el) => {
    const nextHTML = getSectionInnerHTMLWithDataset(
      mainCartParsed,
      ".js-contents",
      el.dataset.content
    );
    if (nextHTML !== "") el.innerHTML = nextHTML;
  });

  const cartForm = cartElement.closest("cart-form");
  if (cartForm) {
    cartForm.classList.toggle("is-empty", parsedState.item_count === 0);
  }
}

function normalizeDiscountyCartWidget(root = document) {
  root.querySelectorAll(".discounty-cart-widget__saving-row").forEach((row) => {
    row.style.setProperty("border", "0", "important");
    row.style.setProperty("box-shadow", "none", "important");
    row.style.setProperty("outline", "0", "important");
  });
}

document.addEventListener("DOMContentLoaded", () => {
  normalizeDiscountyCartWidget();
});

function updateCartEssentialsControls(root = document) {
  root.querySelectorAll("[data-cart-essentials]").forEach((container) => {
    const track = container.querySelector(".cart-essentials__track");
    const prev = container.querySelector("[data-cart-essentials-prev]");
    const next = container.querySelector("[data-cart-essentials-next]");
    if (!track || !prev || !next) return;

    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth - 1);
    prev.disabled = track.scrollLeft <= 1;
    next.disabled = track.scrollLeft >= maxScrollLeft;
    container.classList.toggle("cart-essentials--no-overflow", maxScrollLeft <= 1);
  });
}

function scrollCartEssentials(container, direction) {
  const track = container?.querySelector?.(".cart-essentials__track");
  if (!track) return;
  const prev = container.querySelector("[data-cart-essentials-prev]");
  const next = container.querySelector("[data-cart-essentials-next]");

  const firstProduct = track.querySelector(".cart-essentials__product");
  const productWidth = firstProduct?.getBoundingClientRect?.().width || track.clientWidth * 0.82;
  const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 0) || 0;
  const distance = Math.max(productWidth + gap, track.clientWidth * 0.72);

  if (direction > 0 && prev) prev.disabled = false;
  if (direction < 0 && next) next.disabled = false;

  track.scrollBy({
    left: direction * distance,
    behavior: "smooth",
  });

  [180, 420, 820].forEach((delay) => {
    window.setTimeout(() => updateCartEssentialsControls(container), delay);
  });
}

document.addEventListener("click", (event) => {
  const prev = event.target.closest("[data-cart-essentials-prev]");
  const next = event.target.closest("[data-cart-essentials-next]");
  if (!prev && !next) return;

  const container = event.target.closest("[data-cart-essentials]");
  if (!container) return;

  event.preventDefault();
  scrollCartEssentials(container, prev ? -1 : 1);
});

document.addEventListener("scroll", (event) => {
  if (event.target?.matches?.(".cart-essentials__track")) {
    updateCartEssentialsControls(event.target.closest("[data-cart-essentials]") || document);
  }
}, true);

window.addEventListener("resize", () => updateCartEssentialsControls(), { passive: true });
document.addEventListener("DOMContentLoaded", () => updateCartEssentialsControls());

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      if (node.matches?.(".discounty-portal, .discounty-cart-widget, .discounty-cart-widget__saving-row")) {
        normalizeDiscountyCartWidget(node);
      } else if (node.querySelector?.(".discounty-cart-widget__saving-row")) {
        normalizeDiscountyCartWidget(node);
      }

      if (node.matches?.("[data-cart-essentials]") || node.querySelector?.("[data-cart-essentials]")) {
        updateCartEssentialsControls(node.matches?.("[data-cart-essentials]") ? node.parentElement || document : node);
      }
    });
  }
}).observe(document.documentElement, { childList: true, subtree: true });

document.addEventListener("click", async (event) => {
  const addButton = event.target.closest("[data-cart-essentials-add]");
  if (!addButton) return;

  event.preventDefault();
  if (addButton.disabled || addButton.getAttribute("aria-disabled") === "true") return;

  const variantId = addButton.getAttribute("data-variant-id");
  if (!variantId) return;

  if (window.SkinNoteCartLimit?.wouldExceed?.(variantId, 1)) {
    window.SkinNoteCartLimit.showInlineError(addButton, window.SkinNoteCartLimit.message());
    return;
  }

  const cartDrawer = document.querySelector("cart-drawer");
  const cartElement = document.getElementById("Cart");
  const sections = [];

  if (cartDrawer) {
    cartDrawer.getSectionsToRender().forEach((section) => sections.push(section.id));
    cartDrawer.setActiveElement(addButton);
    cartDrawer.showPending?.(addButton);
  }

  if (cartElement?.dataset.id) sections.push(cartElement.dataset.id);

  addButton.setAttribute("aria-disabled", "true");
  addButton.setAttribute("aria-busy", "true");
  addButton.classList.add("loading");

  try {
    const response = await fetch(`${routes.cart_add_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: Array.from(new Set(sections)),
        sections_url: window.location.pathname,
      }),
    });

    const parsedState = await response.json();
    if (parsedState.status) throw new Error(parsedState.description || parsedState.message);

    renderMainCartFromState(parsedState);

    if (cartDrawer && parsedState.sections?.["cart-drawer"]) {
      cartDrawer.renderContents(parsedState, {
        preserveOpen: cartDrawer.classList.contains("open"),
        scrollToTop: true,
        promotedVariantId: variantId,
      });
    } else {
      const bubble = document.getElementById("cart-icon-bubble");
      if (bubble && parsedState.sections?.["cart-icon-bubble"]) {
        bubble.innerHTML = getSectionInnerHTML(parsedState.sections["cart-icon-bubble"], ".shopify-section");
      }
    }
  } catch (error) {
    console.error(error);
  } finally {
    addButton.classList.remove("loading");
    addButton.removeAttribute("aria-disabled");
    addButton.removeAttribute("aria-busy");
  }
});

// Function to show discount error
function showDiscountError(message, context, type = "code") {
  // Find the cart discount component and use its showError method
  const discountComponent =
    context === "drawer"
      ? document.querySelector('cart-discount-component[context="drawer"]')
      : document.querySelector('cart-discount-component[context="main"]');

  if (discountComponent) {
    discountComponent.showError(type, message);
    const details = discountComponent.querySelector("details");
    if (details) details.setAttribute("open", true);
  } else {
    const errorElement =
      context === "drawer"
        ? document.getElementById("DrawerCart-DiscountError")
        : document.getElementById("Cart-DiscountError");

    if (errorElement) {
      const codeEl = errorElement.querySelector(".cart-discount-error-code");
      const shippingEl = errorElement.querySelector(
        ".cart-discount-error-shipping"
      );
      if (type === "shipping") {
        if (shippingEl) shippingEl.style.display = "block";
        if (codeEl) codeEl.style.display = "none";
      } else {
        if (codeEl) codeEl.style.display = "block";
        if (shippingEl) shippingEl.style.display = "none";
      }
      errorElement.style.display = "block";
      const details = errorElement.closest("details");
      if (details) details.setAttribute("open", true);
    }
  }
}

class CartDiscountRemoval extends HTMLElement {
  constructor() {
    super();
    this.discountCode = this.getAttribute("data-discount-code");
    this.context = this.getAttribute("data-context") || "main";

    const removeLink = this.querySelector("a");
    if (removeLink) {
      removeLink.setAttribute("role", "button");
      removeLink.addEventListener(
        "click",
        this.handleRemoveDiscount.bind(this)
      );
      removeLink.addEventListener("keyup", (event) => {
        event.preventDefault();
        if (event.code.toUpperCase() === "SPACE")
          this.handleRemoveDiscount(event);
      });
    }
  }

  async handleRemoveDiscount(event) {
    event.preventDefault();

    if (!this.discountCode) return;

    try {
      const removeLink = this.querySelector("a");
      if (removeLink) {
        removeLink.style.opacity = "0.5";
        removeLink.style.pointerEvents = "none";
      }

      const existingCodes = getCurrentDiscountCodes(this.context);
      const nextCodes = existingCodes.filter(
        (code) => code !== this.discountCode
      );
      const body = JSON.stringify({
        discount: nextCodes.join(","),
        sections: getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });

      const response = await fetch(`${routes.cart_update_url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: body,
      });

      if (response.ok) {
        const responseText = await response.text();
        const parsedState = JSON.parse(responseText);

        if (parsedState.errors && Object.keys(parsedState.errors).length > 0) {
          throw new Error(parsedState.errors);
        }

        // Update the sections in the cart
        getSectionsToRender().forEach((section) => {
          if (section.id === "Cart") {
            const elementsToReplace = document
              .getElementById(section.id)
              ?.querySelectorAll(section.selector);
            const mainCartParsed =
              parsedState.sections[document.getElementById("Cart")?.dataset.id];
            if (elementsToReplace && mainCartParsed) {
              elementsToReplace.forEach((el) => {
                el.innerHTML = getSectionInnerHTMLWithDataset(
                  mainCartParsed,
                  section.selector,
                  el.dataset.content
                );
              });
            }
            return;
          }

          const sectionElement = document.getElementById(section.id);
          if (sectionElement && parsedState.sections[section.section]) {
            const elementToReplace =
              sectionElement.querySelector(section.selector) || sectionElement;
            elementToReplace.innerHTML = getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          }
        });

        // Dispatch custom event for other components
        document.dispatchEvent(
          new CustomEvent("cart:discount-removed", {
            detail: { discountCode: this.discountCode },
          })
        );
      } else {
        throw new Error("Failed to remove discount");
      }
    } catch (error) {
      // Show error message
      const errorMessage =
        this.context === "drawer"
          ? document.querySelector(
              ".cart-drawer .cart-discount-component .discount-error"
            )
          : document.querySelector(
              ".cart-footer .cart-discount-component .discount-error"
            );

      if (errorMessage) {
        errorMessage.textContent = this.dataset.removeError || "";
        errorMessage.style.display = "block";
        setTimeout(() => {
          errorMessage.style.display = "none";
        }, 3000);
      }
    } finally {
      // Reset loading state
      const removeLink = this.querySelector("a");
      if (removeLink) {
        removeLink.style.opacity = "1";
        removeLink.style.pointerEvents = "auto";
      }
    }
  }
}

customElements.define("cart-discount-removal", CartDiscountRemoval);

if (!customElements.get("cart-discount-component")) {
  customElements.define(
    "cart-discount-component",
    class CartDiscountComponent extends HTMLElement {
      constructor() {
        super();

        this.discountInput = this.querySelector(".cart-discount-input");
        this.discountButton = this.querySelector(".cart-discount-button");
        this.discountDetails = this.querySelector(".cart-discount");
        this.discountSummary = this.discountDetails?.querySelector("summary");
        this.errorContainer = this.querySelector(".cart-discount-error");
        this.errorCodeEl = this.errorContainer?.querySelector(
          ".cart-discount-error-code"
        );
        this.errorShippingEl = this.errorContainer?.querySelector(
          ".cart-discount-error-shipping"
        );

        // Bind event handlers
        this.handleApplyDiscount = this.handleApplyDiscount.bind(this);
        this.handleKeyPress = this.handleKeyPress.bind(this);
        this.keepDiscountOpen = this.keepDiscountOpen.bind(this);
      }

      connectedCallback() {
        this.keepDiscountOpen();

        if (this.discountSummary) {
          this.discountSummary.addEventListener("click", this.keepDiscountOpen);
          this.discountSummary.addEventListener("keydown", this.keepDiscountOpen);
        }

        if (this.discountButton) {
          this.discountButton.addEventListener(
            "click",
            this.handleApplyDiscount
          );
        }

        if (this.discountInput) {
          this.discountInput.addEventListener("keypress", this.handleKeyPress);
        }
      }

      disconnectedCallback() {
        if (this.discountSummary) {
          this.discountSummary.removeEventListener("click", this.keepDiscountOpen);
          this.discountSummary.removeEventListener("keydown", this.keepDiscountOpen);
        }

        if (this.discountButton) {
          this.discountButton.removeEventListener(
            "click",
            this.handleApplyDiscount
          );
        }

        if (this.discountInput) {
          this.discountInput.removeEventListener(
            "keypress",
            this.handleKeyPress
          );
        }
      }

      keepDiscountOpen(event) {
        if (event?.type === "keydown" && !["Enter", " "].includes(event.key)) {
          return;
        }

        event?.preventDefault();
        this.discountDetails?.setAttribute("open", "");
      }

      handleKeyPress(event) {
        if (event.key === "Enter") {
          event.preventDefault();
          this.handleApplyDiscount();
        }
      }

      handleApplyDiscount() {
        const discountCode = this.discountInput?.value?.trim();
        const context = this.getAttribute("context") || "main";

        if (!discountCode) {
          if (this.discountInput) {
            try {
              this.discountInput.reportValidity();
            } catch (e) {}
            this.discountInput.focus();
          }
          return;
        }

        // Clear any existing errors
        this.clearError();

        // Apply the discount
        applyDiscount(discountCode, context);
      }

      showError(type, message) {
        if (!this.errorContainer) return;
        if (type === "shipping") {
          if (this.errorShippingEl)
            this.errorShippingEl.style.display = "block";
          if (this.errorCodeEl) this.errorCodeEl.style.display = "none";
        } else {
          if (this.errorCodeEl) this.errorCodeEl.style.display = "block";
          if (this.errorShippingEl) this.errorShippingEl.style.display = "none";
        }
        this.errorContainer.style.display = "block";
        const details = this.querySelector("details");
        if (details) details.setAttribute("open", true);
      }

      clearError() {
        if (!this.errorContainer) return;
        if (this.errorCodeEl) this.errorCodeEl.style.display = "none";
        if (this.errorShippingEl) this.errorShippingEl.style.display = "none";
        this.errorContainer.style.display = "none";
      }

      clearInput() {
        if (this.discountInput) {
          this.discountInput.value = "";
        }
      }
    }
  );
}

if (!customElements.get("cart-free-shipping-bar")) {
  customElements.define(
    "cart-free-shipping-bar",
    class CartFreeShippingBar extends HTMLElement {
      static get observedAttributes() {
        return ["threshold", "cart-total"];
      }
      constructor() {
        super();

        // Get references to important elements
        this.text = this.querySelector(".free-shipping-text");
        this.progressBar = this.querySelector(".free-shipping-bar span");
      }

      connectedCallback() {
        // Calculate the threshold based on the currency rate
        this.threshold = Math.round(
          this.threshold * (Shopify.currency.rate || 1)
        );
        this.updateShippingIndicator();
        this.updateText();
      }

      // Getters and setters for attributes
      get amountLeft() {
        return this.getAttribute("amount-left");
      }

      set amountLeft(value) {
        this.setAttribute("amount-left", value);
      }

      get threshold() {
        return parseFloat(this.getAttribute("threshold"));
      }

      set threshold(value) {
        this.setAttribute("threshold", value);
      }

      get cartTotal() {
        return parseFloat(this.getAttribute("cart-total"));
      }

      set cartTotal(value) {
        this.setAttribute("cart-total", value);
      }

      // Update the shipping indicator progress bar
      updateShippingIndicator() {
        const percentage = Math.round(
          (this.cartTotal * 100.0) / this.threshold
        );
        // Limit the percentage value to 100 if it exceeds that value
        const clampedPercentage = Math.min(percentage, 100);

        this.progressBar.style.setProperty("--width", `${clampedPercentage}%`);
      }

      // Update the text based on the cart total and threshold
      updateText() {
        if (this.cartTotal >= this.threshold) {
          this.text.innerHTML = this.dataset.unlockedMessage;
        } else {
          this.text.innerHTML = this.dataset.amountLeftMessage.replace(
            "{{ amount }}",
            this.amountLeft
          );
        }
      }
    }
  );
}
